import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import { ErrorField } from '@/components/error-field'
import * as Input from '@/components/input'
import { SideImageForm } from '@/components/side-image-form'
import { Textarea } from '@/components/textarea'
import { useAuth } from '@/contexts'
import {
  contactFormValidationSchema,
  type ContactFormValues,
} from '@/schemas/contact-schema'
import api from '@/services/api'

export function Contact() {
  const { nameUser, email } = useAuth()

  const { register, handleSubmit, formState, reset, watch } =
    useForm<ContactFormValues>({
      resolver: zodResolver(contactFormValidationSchema),
      defaultValues: {
        nameAndLastName: nameUser ?? '',
        email: email ?? '',
        subject: '',
        message: '',
      },
    })

  const messageLength = watch('message').length

  const navigate = useNavigate()

  async function handleContact(data: ContactFormValues) {
    await api
      .post('/sendmail/contact', data)
      .then(() => {
        navigate('/contact-message')
        reset()
      })
      .catch((error) => {
        toast.error(error.response.data.message)
      })
  }

  return (
    <div className="grid flex-1 grid-cols-1 bg-monochromatic-200 lg:grid-cols-9">
      <SideImageForm title="Entre em contato conosco" className="bg-contact" />

      <div className="my-auto px-10 py-10 md:px-20 lg:col-span-4 xl:px-36 2xl:px-56">
        <h1 className="mb-6 text-center text-3xl font-bold text-bios-title lg:text-start xl:text-4xl">
          Entre em contato com a gente e comece hoje mesmo a ser um guardião do
          meio ambiente
        </h1>
        <form onSubmit={handleSubmit(handleContact)} className="space-y-4">
          <div>
            <Input.Label htmlFor="nameAndLastName" mandatory>
              Nome e sobrenome
            </Input.Label>
            <Input.Root withError={!!formState.errors.nameAndLastName}>
              <Input.Control
                id="nameAndLastName"
                type="text"
                placeholder="Digite seu nome completo"
                {...register('nameAndLastName')}
              />
            </Input.Root>
            {formState.errors.nameAndLastName && (
              <ErrorField>
                {formState.errors.nameAndLastName.message}
              </ErrorField>
            )}
          </div>

          <div>
            <Input.Label htmlFor="email" mandatory>
              E-mail
            </Input.Label>
            <Input.Root withError={!!formState.errors.email}>
              <Input.Control
                id="email"
                type="text"
                placeholder="Digite seu e-mail"
                {...register('email')}
              />
            </Input.Root>
            {formState.errors.email && (
              <ErrorField>{formState.errors.email.message}</ErrorField>
            )}
          </div>

          <div>
            <Input.Label htmlFor="subject" mandatory>
              Assunto
            </Input.Label>
            <Input.Root withError={!!formState.errors.subject}>
              <Input.Control
                id="subject"
                type="text"
                placeholder="Digite o assunto"
                {...register('subject')}
              />
            </Input.Root>
            {formState.errors.subject && (
              <ErrorField>{formState.errors.subject.message}</ErrorField>
            )}
          </div>

          <div>
            <Input.Label htmlFor="message" mandatory>
              Escreva uma mensagem
            </Input.Label>
            <Textarea
              id="message"
              placeholder="Digite uma mensagem"
              maxLength={1000}
              withError={!!formState.errors.message}
              count={messageLength}
              limit={1000}
              {...register('message')}
            />
            {formState.errors.message && (
              <ErrorField>{formState.errors.message.message}</ErrorField>
            )}
          </div>

          <div>
            <Button
              className="mt-2 w-full"
              type="submit"
              disabled={formState.isSubmitting}
              isSubmitting={formState.isSubmitting}
            >
              Enviar mensagem
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
